import React, { useState } from "react";
import { useAuth } from "react-oidc-context";

export default function HdiRegistration() {
  const auth = useAuth();
  const [file, setFile] = useState(null);
  const [parsedJson, setParsedJson] = useState(null);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [serverResponse, setServerResponse] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [dev, setDev] = useState(false);
  const [stdoutOutput, setStdoutOutput] = useState("");
  const [stderrOutput, setStderrOutput] = useState("");

  const apiUrl = window.APP_CONFIG?.REACT_APP_API_URL || process.env.REACT_APP_API_URL;

  const handleStreamMessage = (message) => {
    if (message.type === "stdout") {
      setStdoutOutput((prev) => prev + message.data + "\n");
    } else if (message.type === "stderr") {
      setStderrOutput((prev) => prev + message.data + "\n");
    } else if (message.type === "summary") {
      setServerResponse(message.data);
      const returnCode = message.data?.processing?.return_code;
      if (typeof returnCode === "number" && returnCode !== 0) {
        setError("HDI registration failed");
        setStatus("Registration failed");
        return "error";
      }
      setStatus("Registration succeeded");
      return "success";
    } else if (message.type === "error") {
      const errorData = message.data;
      if (errorData?.summary) {
        setServerResponse(errorData.summary);
      }
      setError(
        typeof errorData === "string"
          ? errorData
          : errorData?.message || JSON.stringify(errorData, null, 2)
      );
      setStatus("Registration failed");
      return "error";
    } else if (message.type === "heartbeat") {
      console.log("Received server heartbeat.");
    }
    return null;
  };

  const onFileChange = (e) => {
    setStatus("");
    setError("");
    setParsedJson(null);
    setServerResponse(null);
    setStdoutOutput("");
    setStderrOutput("");
    const f = e.target.files && e.target.files[0];
    if (!f) {
      setFile(null);
      return;
    }
    if (!f.name.toLowerCase().endsWith(".json") && f.type !== "application/json") {
      setError("Please select a JSON file.");
      setFile(null);
      return;
    }
    setFile(f);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const json = JSON.parse(reader.result);
        setParsedJson(json);
      } catch (err) {
        setParsedJson(null);
        setError("Invalid JSON file: " + err.message);
      }
    };
    reader.onerror = () => {
      setError("Failed to read file.");
      setFile(null);
    };
    reader.readAsText(f);
  };

  const onUpload = async () => {
    setStatus("");
    setError("");
    setServerResponse(null);
    setStdoutOutput("");
    setStderrOutput("");
    if (!file) {
      setError("No file selected.");
      return;
    }
    if (!auth.isAuthenticated) {
      setError("You must be authenticated to upload.");
      return;
    }

    try {
      const form = new FormData();
      form.append("file", file);
      form.append("dev", dev ? "true" : "false");

      setIsProcessing(true);
      setStatus("Uploading and registering (this may take a while)...");

      const res = await fetch(`${apiUrl}/api/v1/hdi-registration/upload`, {
        method: "POST",
        body: form,
        headers: {
          Authorization: `Bearer ${auth.user?.access_token}`,
          Accept: "application/x-ndjson",
        },
        credentials: "include",
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Upload failed: ${res.status} ${text}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let terminalOutcome = null;

      const processStreamLine = (line) => {
        if (line.trim() === "") return;
        const outcome = handleStreamMessage(JSON.parse(line));
        if (outcome === "error" || (outcome === "success" && terminalOutcome === null)) {
          terminalOutcome = outcome;
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }
        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split("\n");
        buffer = lines.pop();

        for (const line of lines) {
          if (line.trim() === "") continue;
          try {
            processStreamLine(line);
          } catch (parseError) {
            console.error("Failed to parse JSON stream message:", parseError, "Line:", line);
          }
        }
      }

      if (buffer.trim() !== "") {
        try {
          processStreamLine(buffer);
        } catch (parseError) {
          console.error("Failed to parse final JSON stream message:", parseError, "Line:", buffer);
        }
      }

      if (terminalOutcome === null) {
        throw new Error("Registration failed: the server response ended without a final result.");
      }
    } catch (err) {
      setError(err.message || "Upload failed");
      setStatus("Registration failed");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div style={{ padding: 20, maxWidth: 800, margin: "0 auto", textAlign: "left" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <h2>HDI Registration - Upload JSON</h2>
        <a href="/help#hdi-registration">Help</a>
      </div>
      {!auth.isAuthenticated ? (
        <>
          <p>You must be logged in to upload a file.</p>
          <button
            onClick={() => {
              const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
              sessionStorage.setItem("post_login_redirect", returnTo);
              auth.signinRedirect({ state: { returnTo } });
            }}
          >
            Log in
          </button>
        </>
      ) : (
        <>
          <p>Select a JSON file that will be processed by the backend.</p>
          <label style={{ display: "block", marginBottom: 8 }}>
            <input
              type="checkbox"
              checked={dev}
              onChange={(e) => setDev(e.target.checked)}
              style={{ marginRight: 8 }}
            />
            Use development database
          </label>
          <input
            aria-label="HDI JSON file"
            type="file"
            accept=".json,application/json"
            onChange={onFileChange}
          />
          {error && (
            <p role="alert" style={{ color: "red", fontWeight: 600 }}>
              {error}
            </p>
          )}
          {parsedJson && (
            <>
              <h3>Preview</h3>
              <pre style={{ background: "#f7f7f7", padding: 10, maxHeight: 300, overflow: "auto" }}>
                {JSON.stringify(parsedJson, null, 2)}
              </pre>
            </>
          )}
          <div style={{ marginTop: 12 }}>
            <button onClick={onUpload} disabled={!file || !!error || isProcessing}>
              Upload JSON
            </button>
            {status && (
              <span
                style={{
                  marginLeft: 12,
                  color:
                    status === "Registration failed"
                      ? "darkred"
                      : status === "Registration succeeded"
                        ? "darkgreen"
                        : "inherit",
                  fontWeight: status.startsWith("Registration ") ? 700 : "normal",
                }}
              >
                {status}
              </span>
            )}
            {isProcessing && <span style={{ marginLeft: 12 }}>⏳</span>}
          </div>

          {(stdoutOutput || stderrOutput || isProcessing) && (
            <>
              <h3>Processing output</h3>
              {stdoutOutput && (
                <>
                  <h4>Stdout</h4>
                  <pre
                    style={{
                      background: "#fff",
                      padding: 10,
                      maxHeight: 300,
                      overflow: "auto",
                      textAlign: "left",
                    }}
                  >
                    {stdoutOutput}
                  </pre>
                </>
              )}
              {stderrOutput && (
                <>
                  <h4>Stderr</h4>
                  <pre
                    style={{
                      background: "#fff",
                      padding: 10,
                      maxHeight: 200,
                      overflow: "auto",
                      textAlign: "left",
                      color: "darkred",
                    }}
                  >
                    {stderrOutput}
                  </pre>
                </>
              )}
              {isProcessing && !stdoutOutput && !stderrOutput && (
                <p>Waiting for processing output...</p>
              )}
            </>
          )}

          {serverResponse && (
            <>
              <h3>{error ? "Registration failure details" : "Registration result"}</h3>
              <pre
                style={{
                  background: error ? "#fff1f0" : "#f0f7f0",
                  padding: 10,
                  maxHeight: 400,
                  overflow: "auto",
                  textAlign: "left",
                }}
              >
                {JSON.stringify(serverResponse, null, 2)}
              </pre>
            </>
          )}
        </>
      )}
    </div>
  );
}
